// Revisión rápida de cascos: el cliente confirma los "sin casco" de la IA.
// Con eso se mide la tasa de falsas alarmas, se corrige el % de todo el período
// y quedan etiquetas listas para reentrenar el modelo.
import React, { useEffect, useState } from 'react';
import { photoUrl, getFix, setFix, helmetInfo, exportFixes, fmtDMY, fmtHM } from './iaData';
import { T, Btn } from './ui';

const PAGE_PHOTOS = 8;

export default function HelmetReview({ photos, cfg, cam, onChange, onClose }) {
  // Fotos con "sin casco" de la IA todavía sin revisar, repartidas en el período
  // Se arma una sola vez al abrir: lo que se va marcando queda en pantalla (no salta la grilla)
  const [pool] = useState(() => {
    const c = photos
      .map((p) => ({ p, dets: p.dets.filter((d) => d.cls === 'Persona sin casco' && d.x1 != null && d.conf >= (cfg.thr[d.cls] ?? 0) && !getFix(p, d)) }))
      .filter((x) => x.dets.length);
    const step = Math.max(1, Math.floor(c.length / 40));
    return c.filter((_, i) => i % step === 0);
  });
  const [page, setPage] = useState(0);
  const batch = pool.slice(page * PAGE_PHOTOS, (page + 1) * PAGE_PHOTOS);
  const [urls, setUrls] = useState({});
  const [, force] = useState(0);
  const info = helmetInfo();

  useEffect(() => {
    batch.forEach(({ p }) => { if (!urls[p.id]) photoUrl(p).then((u) => setUrls((m) => ({ ...m, [p.id]: u }))).catch(() => {}); });
  }, [page, pool.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const mark = (p, d, val) => { setFix(p, d, getFix(p, d)?.val === val ? null : val); force((x) => x + 1); onChange(); };
  const crops = batch.flatMap(({ p, dets }) => dets.map((d) => ({ p, d })));
  const pendientes = crops.filter(({ p, d }) => !getFix(p, d)).length;

  const download = () => {
    const blob = new Blob([JSON.stringify({ camara: cam, generado: new Date().toISOString(), etiquetas: exportFixes() }, null, 2)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `correcciones-casco-${cam}.json`; a.click();
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1000, display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', color: T.text, borderRadius: 20, boxShadow: '0 25px 50px rgba(0,0,0,.25)', width: 'min(1100px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '18px 20px 12px', borderBottom: `1px solid ${T.line}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18 }}>Revisar cascos</h3>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: T.muted, maxWidth: 640, lineHeight: 1.5 }}>
                Estas son personas que la IA marcó <b style={{ color: T.red }}>sin casco</b>. Tocá lo que ves. Con unas 20 revisadas el % de casco se corrige solo para todo el período, y lo que marques sirve para entrenar mejor el modelo.
              </p>
            </div>
            <div style={{ textAlign: 'right', fontSize: 13 }}>
              <div><b>{info.reviewed}</b> {info.reviewed === 1 ? 'revisada' : 'revisadas'}</div>
              {info.reviewed > 0 && <div style={{ color: T.muted }}>{Math.round((100 * info.falsas) / info.reviewed)}% eran falsas alarmas</div>}
              <div style={{ color: info.rate != null ? T.green : T.muted, fontWeight: 600 }}>{info.rate != null ? '✓ Corrección activa' : `Faltan ${Math.max(0, 8 - info.reviewed)} para activar la corrección`}</div>
            </div>
          </div>
        </div>

        <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
          {!crops.length ? (
            <div style={{ color: T.muted, textAlign: 'center', padding: 30 }}>No quedan alertas de "sin casco" para revisar en este período. 🎉</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {crops.map(({ p, d }) => {
                const f = getFix(p, d)?.val;
                return (
                  <div key={p.id + d.x1 + d.y1} style={{ border: `2px solid ${f === 'con' ? T.green : f === 'sin' ? T.red : f === 'no' ? '#9ca3af' : T.line}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <BigCrop p={p} d={d} url={urls[p.id]} />
                    <div style={{ fontSize: 11, color: T.muted, padding: '4px 8px' }}>{fmtDMY(p.ms)} {fmtHM(p.ms)} · IA {d.conf.toFixed(2)}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, padding: '0 6px 6px' }}>
                      <button type="button" onClick={() => mark(p, d, 'con')} style={btn(f === 'con', T.green)}>Tiene casco</button>
                      <button type="button" onClick={() => mark(p, d, 'sin')} style={btn(f === 'sin', T.red)}>Sin casco</button>
                      <button type="button" onClick={() => mark(p, d, 'no')} style={{ ...btn(f === 'no', '#6b7280'), gridColumn: '1 / 3' }}>No es una persona</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap', padding: '12px 20px', borderTop: `1px solid ${T.line}` }}>
          <Btn onClick={download} disabled={!info.total} title="Etiquetas para reentrenar el modelo de casco">⬇ Descargar correcciones ({info.total})</Btn>
          <div style={{ display: 'flex', gap: 8 }}>
            {(page + 1) * PAGE_PHOTOS < pool.length && <Btn onClick={() => setPage(page + 1)}>{pendientes ? 'Saltear y ver más' : 'Ver más'}</Btn>}
            <Btn active onClick={onClose}>Listo</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

const btn = (on, c) => ({ padding: '5px 4px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${c}`, background: on ? c : '#fff', color: on ? '#fff' : c });

function BigCrop({ p, d, url }) {
  const padX = (d.x2 - d.x1) * 0.35, padY = (d.y2 - d.y1) * 0.2;
  const x1 = Math.max(0, d.x1 - padX), y1 = Math.max(0, d.y1 - padY);
  const bw = Math.min(p.w, d.x2 + padX) - x1, bh = Math.min(p.h, d.y2 + padY) - y1;
  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: `${bw} / ${bh}`, maxHeight: 260, background: '#111', overflow: 'hidden' }}>
      {url && (
        <div style={{
          position: 'absolute', inset: 0, backgroundImage: `url("${url}")`, backgroundRepeat: 'no-repeat',
          backgroundSize: `${(p.w / bw) * 100}% ${(p.h / bh) * 100}%`,
          backgroundPosition: `${(x1 / (p.w - bw || 1)) * 100}% ${(y1 / (p.h - bh || 1)) * 100}%`,
        }} />
      )}
      {!url && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#9ca3af', fontSize: 12 }}>Cargando…</div>}
    </div>
  );
}
